import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { 
  Search, 
  SlidersHorizontal, 
  ChevronLeft, 
  ChevronRight, 
  Play,
  Bookmark,
  Zap,
  Users,
  Calendar,
  Grid3X3,
  List,
  RotateCcw
} from 'lucide-react'
import { getAllGames, getGameUrl, getPlayUrl } from '@/data/games'
import { SEO, BreadcrumbStructuredData } from '@/components/seo/SEO'

// Available categories for filtering
const categories = [
  { id: 'action', name: 'Action', checked: false },
  { id: 'arcade', name: 'Arcade', checked: false },
  { id: 'puzzle', name: 'Puzzle', checked: false },
  { id: 'racing', name: 'Racing', checked: false },
  { id: 'adventure', name: 'Adventure', checked: false },
  { id: 'simulation', name: 'Simulation', checked: false },
  { id: 'strategy', name: 'Strategy', checked: false },
]

// Performance filters
const performanceOptions = [
  { id: '60fps', name: '60+ FPS', active: false },
  { id: '30fps', name: '30 FPS', active: false },
  { id: 'highend', name: 'High-End Only', active: false },
]

// Era filters
const eraOptions = [
  { id: 'all', name: 'All Eras' },
  { id: 'modern', name: 'Modern Era (2024-Present)' },
  { id: 'classic', name: 'Classic Era (2020-2023)' },
  { id: 'legacy', name: 'Legacy Era (2017-2019)' },
]

// Sort options
const sortOptions = [
  { id: 'relevant', name: 'Most Relevant' },
  { id: 'popular', name: 'Most Popular' },
  { id: 'newest', name: 'Newest First' },
  { id: 'rating', name: 'Highest Rated' },
]

// Pagination constants
const GAMES_PER_PAGE = 8

// Game Card Component
function GameCard({ game, viewMode = 'list' }) {
  if (viewMode === 'grid') {
    return (
      <Link 
        to={getGameUrl(game)}
        className="glass-panel group rounded-xl overflow-hidden hover:border-game-primary/50 transition-all cursor-pointer flex flex-col"
      >
        {/* Thumbnail */}
        <div className="aspect-video w-full bg-card-dark overflow-hidden relative">
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent z-10" />
          <div className="absolute bottom-2 left-2 flex gap-1 z-20">
            <span className="px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-[10px] font-bold text-white border border-white/10 uppercase">
              {game.engine || 'WebGL'}
            </span>
          </div>
          {game.playable && (
            <div className="absolute top-2 right-2 z-20 px-2 py-0.5 rounded-full bg-green-500/90 backdrop-blur text-[10px] font-bold text-white flex items-center gap-1">
              <Zap className="w-3 h-3" /> PLAYABLE
            </div>
          )}
          <img 
            src={game.image} 
            alt={game.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
          {/* Play overlay */}
          <div className="absolute inset-0 z-20 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <div className="bg-game-primary text-black font-bold p-3 rounded-full transform scale-90 group-hover:scale-100 transition-transform">
              <Play className="w-6 h-6 fill-current" />
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="p-4 flex flex-col gap-2 flex-1">
          <h3 className="text-base font-bold group-hover:text-game-primary transition-colors line-clamp-1">
            {game.title}
          </h3>
          <p className="text-white/50 text-xs line-clamp-2 flex-1">{game.shortDescription}</p>
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
            <div className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-game-primary" />
              <span className="text-[11px] text-white/60">{game.plays || game.activePlayers || '1k'}</span>
            </div>
            <span className="text-[11px] text-game-primary font-bold">{game.rating} ★</span>
          </div>
        </div>
      </Link>
    )
  }

  return (
    <Link 
      to={getGameUrl(game)}
      className="glass-panel group rounded-xl p-4 flex flex-col sm:flex-row gap-6 hover:border-game-primary/50 transition-all cursor-pointer"
    >
      {/* Thumbnail */}
      <div className="w-full sm:w-48 h-32 rounded-lg bg-card-dark overflow-hidden relative flex-shrink-0">
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent z-10" />
        <div className="absolute bottom-2 left-2 flex gap-1 z-20">
          <span className="px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-[10px] font-bold text-white border border-white/10 uppercase">
            {game.engine || 'WebGL'}
          </span>
        </div>
        <img 
          src={game.image} 
          alt={game.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
      </div>

      {/* Info */}
      <div className="flex-1 flex flex-col justify-between">
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold group-hover:text-game-primary transition-colors">
              {game.title}
            </h3>
            <span className="text-white/30 text-xs">ID: #{game.id?.slice(-5)}</span>
          </div>
          <p className="text-white/60 text-sm line-clamp-1">{game.shortDescription}</p>
        </div>
        <div className="flex items-center gap-4 mt-4">
          <div className="flex items-center gap-1.5">
            <Zap className="w-4 h-4 text-game-primary" />
            <span className="text-xs font-medium text-white/80">{game.fps || '60'} FPS</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Users className="w-4 h-4 text-game-primary" />
            <span className="text-xs font-medium text-white/80">{game.plays || game.activePlayers || '1k'} Plays</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Calendar className="w-4 h-4 text-game-primary" />
            <span className="text-xs font-medium text-white/80">{game.releaseDate || 'Jan 2024'}</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex sm:flex-col items-center justify-center gap-3" onClick={e => e.preventDefault()}>
        <Link 
          to={game.playable ? getPlayUrl(game) : getGameUrl(game)}
          className="size-12 rounded-full bg-game-primary flex items-center justify-center text-black shadow-lg shadow-game-primary/20 hover:scale-110 transition-transform"
        >
          <Play className="w-5 h-5 fill-current ml-0.5" />
        </Link>
        <button className="size-10 rounded-full border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:border-white/30 transition-all">
          <Bookmark className="w-4 h-4" />
        </button>
      </div>
    </Link>
  )
}

// Pagination Component
function Pagination({ currentPage, totalPages, onPageChange }) {
  const getPageNumbers = () => {
    const pages = []
    const showEllipsisStart = currentPage > 3
    const showEllipsisEnd = currentPage < totalPages - 2

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      pages.push(1)
      if (showEllipsisStart) pages.push('...')
      
      const start = Math.max(2, currentPage - 1)
      const end = Math.min(totalPages - 1, currentPage + 1)
      
      for (let i = start; i <= end; i++) pages.push(i)
      
      if (showEllipsisEnd) pages.push('...')
      pages.push(totalPages)
    }
    return pages
  }

  return (
    <div className="flex items-center justify-center gap-2 py-6">
      <button 
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        className="size-10 rounded-lg glass-panel flex items-center justify-center text-white/40 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      
      {getPageNumbers().map((page, idx) => (
        page === '...' ? (
          <span key={`ellipsis-${idx}`} className="px-2 text-white/20">...</span>
        ) : (
          <button 
            key={page}
            onClick={() => onPageChange(page)}
            className={`size-10 rounded-lg font-medium text-sm transition-colors ${
              currentPage === page 
                ? 'bg-game-primary text-black font-bold' 
                : 'glass-panel text-white/60 hover:bg-white/10'
            }`}
          >
            {page}
          </button>
        )
      ))}
      
      <button 
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        className="size-10 rounded-lg glass-panel flex items-center justify-center text-white/40 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  )
}

export function GamesPage() {
  // State
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategories, setSelectedCategories] = useState([])
  const [selectedPerformance, setSelectedPerformance] = useState([])
  const [selectedEra, setSelectedEra] = useState('all')
  const [sortBy, setSortBy] = useState('relevant')
  const [viewMode, setViewMode] = useState('list') // 'list' or 'grid'
  const [currentPage, setCurrentPage] = useState(1)
  const [showSortDropdown, setShowSortDropdown] = useState(false)

  // Get all games
  const allGames = getAllGames()

  // Filter and sort games
  const filteredGames = useMemo(() => {
    let result = [...allGames]

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      result = result.filter(game => 
        game.title.toLowerCase().includes(query) ||
        game.description.toLowerCase().includes(query) ||
        game.genre.toLowerCase().includes(query) ||
        game.tags?.some(tag => tag.toLowerCase().includes(query))
      )
    }

    // Category filter
    if (selectedCategories.length > 0) {
      result = result.filter(game => 
        selectedCategories.some(cat => 
          game.genre.toLowerCase() === cat.toLowerCase() ||
          game.tags?.some(tag => tag.toLowerCase() === cat.toLowerCase())
        )
      )
    }

    // Sort
    switch (sortBy) {
      case 'popular':
        result.sort((a, b) => {
          const aPlays = parseFloat(a.reviews?.replace('k', '000').replace('.', '') || 0)
          const bPlays = parseFloat(b.reviews?.replace('k', '000').replace('.', '') || 0)
          return bPlays - aPlays
        })
        break
      case 'newest':
        result.sort((a, b) => (b.releaseDate || '').localeCompare(a.releaseDate || ''))
        break
      case 'rating':
        result.sort((a, b) => (b.rating || 0) - (a.rating || 0))
        break
      default:
        // Keep default order
        break
    }

    return result
  }, [allGames, searchQuery, selectedCategories, selectedPerformance, selectedEra, sortBy])

  // Pagination
  const totalPages = Math.ceil(filteredGames.length / GAMES_PER_PAGE)
  const paginatedGames = useMemo(() => {
    const start = (currentPage - 1) * GAMES_PER_PAGE
    return filteredGames.slice(start, start + GAMES_PER_PAGE)
  }, [filteredGames, currentPage])

  // Handlers
  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]
    )
    setCurrentPage(1)
  }

  const handleResetFilters = () => {
    setSearchQuery('')
    setSelectedCategories([])
    setSelectedPerformance([])
    setSelectedEra('all')
    setSortBy('relevant')
    setCurrentPage(1)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Player tags from all games
  const allTags = useMemo(() => {
    const tags = new Set()
    allGames.forEach(game => game.tags?.forEach(tag => tags.add(tag)))
    return Array.from(tags).slice(0, 8)
  }, [allGames])

  // Breadcrumb data for SEO
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Games', url: '/games' }
  ]

  return (
    <>
      <SEO 
        title="Games"
        description="Browse and play our complete collection of free browser & downloadable games. Filter by category, search, and find your next favorite game."
        url="/games"
      />
      <BreadcrumbStructuredData items={breadcrumbs} />
      
      {/* Hero Banner - Full Width Outside Container */}
      <div className="relative w-full h-48 md:h-[40vh] overflow-hidden">
        <img 
          src="/images/32105.webp" 
          alt="Games Archive Banner"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-center max-w-[1400px] mx-auto px-6 lg:px-20 pt-28">
          <h2 className="text-3xl md:text-5xl font-black tracking-tight text-white mb-2">Games</h2>
          <p className="text-white/70 text-sm md:text-base max-w-md">
            Discover and play {filteredGames.length} amazing games in the Polytoria ecosystem
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-[1400px] mx-auto px-6 lg:px-20 py-20">
        {/* Main Content - Sidebar + Games */}
        <div className="flex flex-col md:flex-row gap-8">
          {/* Filter Sidebar */}
          <aside className="w-full md:w-80 flex-shrink-0 flex flex-col gap-6">
            <div className="glass-panel p-6 rounded-xl flex flex-col gap-8 sticky top-28">
              <div className="flex flex-col gap-1">
                <h1 className="text-xl font-bold">Advanced Filters</h1>
              <p className="text-white/50 text-xs">Refine your search through time</p>
            </div>

            {/* Category */}
            <div className="flex flex-col gap-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-game-primary/80">Category</h3>
              <div className="flex flex-col gap-2">
                {categories.map(category => (
                  <label key={category.id} className="flex items-center gap-3 group cursor-pointer">
                    <input 
                      type="checkbox"
                      checked={selectedCategories.includes(category.id)}
                    onChange={() => handleCategoryToggle(category.id)}
                    className="rounded border-white/20 bg-card-dark text-game-primary focus:ring-game-primary"
                  />
                  <span className="text-sm text-white/80 group-hover:text-white transition-colors">
                    {category.name}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Performance Rating */}
          <div className="flex flex-col gap-3">
            <h3 className="text-sm font-bold uppercase tracking-wider text-game-primary/80">Performance</h3>
            <div className="flex flex-wrap gap-2">
              {performanceOptions.map(perf => (
                <button 
                  key={perf.id}
                  onClick={() => setSelectedPerformance(prev => 
                    prev.includes(perf.id) ? prev.filter(p => p !== perf.id) : [...prev, perf.id]
                  )}
                  className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${
                    selectedPerformance.includes(perf.id)
                      ? 'bg-game-primary text-black'
                      : 'bg-card-dark text-white/60 hover:bg-white/10'
                  }`}
                >
                  {perf.name}
                </button>
              ))}
            </div>
          </div>

          {/* Release Date */}
          <div className="flex flex-col gap-3">
            <h3 className="text-sm font-bold uppercase tracking-wider text-game-primary/80">Release Date</h3>
            <select 
              value={selectedEra}
              onChange={(e) => setSelectedEra(e.target.value)}
              className="bg-card-dark border-white/10 rounded-lg text-sm text-white focus:ring-game-primary focus:border-game-primary"
            >
              {eraOptions.map(era => (
                <option key={era.id} value={era.id}>{era.name}</option>
              ))}
            </select>
          </div>

          {/* Player Tags */}
          <div className="flex flex-col gap-3">
            <h3 className="text-sm font-bold uppercase tracking-wider text-game-primary/80">Player Tags</h3>
            <div className="flex flex-wrap gap-2">
              {allTags.map(tag => (
                <span 
                  key={tag}
                  onClick={() => handleCategoryToggle(tag.toLowerCase())}
                  className={`px-2 py-1 rounded text-[10px] cursor-pointer transition-colors ${
                    selectedCategories.includes(tag.toLowerCase())
                      ? 'border border-game-primary text-game-primary'
                      : 'bg-white/5 border border-white/10 text-white/70 hover:border-white/30'
                  }`}
                >
                  #{tag.toLowerCase()}
                </span>
              ))}
            </div>
          </div>

          <button 
            onClick={handleResetFilters}
            className="mt-4 w-full py-3 rounded-lg bg-game-primary/10 border border-game-primary/30 text-game-primary text-sm font-bold hover:bg-game-primary hover:text-black transition-all flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Reset All Filters
          </button>
        </div>
      </aside>

      {/* Content Area */}
      <section className="flex-1 flex flex-col gap-6">
        {/* Page Heading & Search */}
        <div className="flex flex-col gap-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <p className="text-white/60">
              Showing {paginatedGames.length} of {filteredGames.length} games
            </p>
            <div className="flex items-center gap-3 text-sm relative">
              <span className="text-white/40">Sort by:</span>
              <button 
                className="flex items-center gap-1 text-game-primary"
                onClick={() => setShowSortDropdown(!showSortDropdown)}
              >
                {sortOptions.find(s => s.id === sortBy)?.name}
                <ChevronLeft className={`w-4 h-4 transition-transform ${showSortDropdown ? 'rotate-90' : '-rotate-90'}`} />
              </button>
              {showSortDropdown && (
                <div className="absolute top-full right-0 mt-2 glass-panel rounded-lg overflow-hidden min-w-40 z-50">
                  {sortOptions.map(option => (
                    <button
                      key={option.id}
                      onClick={() => {
                        setSortBy(option.id)
                        setShowSortDropdown(false)
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                        sortBy === option.id ? 'text-game-primary' : 'text-white/80'
                      }`}
                    >
                      {option.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div className="glass-panel p-2 rounded-xl flex items-center gap-4">
            <div className="flex-1 flex items-center bg-card-dark/50 rounded-lg px-4 py-3 gap-3 border border-white/5">
              <Search className="w-5 h-5 text-white/40" />
              <input 
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setCurrentPage(1)
                }}
                placeholder="Search games by name, genre, or tags..."
                className="bg-transparent border-none focus:ring-0 focus:outline-none text-white w-full placeholder:text-white/30"
              />
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <button 
                onClick={() => setViewMode('list')}
                className={`p-3 rounded-lg transition-colors ${
                  viewMode === 'list' ? 'bg-game-primary text-black' : 'bg-card-dark text-white/60 hover:bg-card-dark/80'
                }`}
              >
                <List className="w-5 h-5" />
              </button>
              <button 
                onClick={() => setViewMode('grid')}
                className={`p-3 rounded-lg transition-colors ${
                  viewMode === 'grid' ? 'bg-game-primary text-black' : 'bg-card-dark text-white/60 hover:bg-card-dark/80'
                }`}
              >
                <Grid3X3 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' : 'flex flex-col gap-4'}>
          {paginatedGames.length > 0 ? (
            paginatedGames.map(game => (
              <GameCard key={game.id} game={game} viewMode={viewMode} />
            ))
          ) : (
            <div className="glass-panel rounded-xl p-12 text-center">
              <Search className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white/80 mb-2">No games found</h3>
              <p className="text-white/50 text-sm">Try adjusting your filters or search query</p>
              <button 
                onClick={handleResetFilters}
                className="mt-4 px-6 py-2 rounded-lg bg-game-primary text-black font-bold text-sm hover:bg-game-primary-hover transition-colors"
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination 
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
        </section>
      </div>
    </main>
    </>
  )
}
